'use client';

import DOMPurify from 'isomorphic-dompurify';
import { Avatar, Modal, Separator } from '@mokoboko/components';

import { detailPath } from '@/helpers/offering';
import { useOpenSitePage } from '@/hooks/use-open-site-page';
import { getPrimaryImage } from '@/lib/entity-image';
import { getFullName } from '@/lib/utils';
import { DESCRIPTION_PROSE } from '@/lib/prose';
import { AdminUser } from '@/state/admin-users';
import { Class } from '@/state/class-groups';
import { ClassRowCard } from './class-row-card';

export const InstructorBioPanel = ({
  instructor,
  classes,
  onClose,
}: {
  instructor: AdminUser | null;
  classes: Class[];
  onClose: () => void;
}) => {
  const openPage = useOpenSitePage();
  const name = instructor
    ? getFullName(instructor.first_name, instructor.last_name)
    : '';
  const classesTitle = `Book a class with ${(instructor?.first_name ?? '').trim()}`;

  const openClass = (slug: string) => {
    onClose();
    openPage(detailPath('class', slug), 'Allow pop-ups to open this class.');
  };

  return (
    <Modal.Root
      open={!!instructor}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Modal.Content size="full" hasCloseButton>
        <Modal.Header>
          <Modal.Title className="text-sm">About instructor</Modal.Title>
        </Modal.Header>
        <Modal.Body className="flex flex-col items-start p-5 sm:p-6">
          {instructor ? (
            <>
              <Avatar
                size="2xl"
                className="mb-5 size-20"
                src={instructor.profile_picture ?? undefined}
                name={name}
              />
              <div className="flex flex-col items-start gap-3">
                <h2 className="heading-page">
                  {name}
                </h2>
                {instructor.bio ? (
                  <div
                    className={DESCRIPTION_PROSE}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(instructor.bio),
                    }}
                  />
                ) : null}
              </div>

              {classes.length > 0 ? (
                <>
                  <div className="w-full py-5">
                    <Separator />
                  </div>
                  <p className="mb-3 heading-sub">
                    {classesTitle}
                  </p>
                  <div className="grid w-full grid-cols-1 gap-3">
                    {classes.map((cls) => (
                      <ClassRowCard
                        key={cls.class_id}
                        name={cls.name}
                        imageUrl={getPrimaryImage(cls.images)?.url ?? cls.image}
                        onClick={() => openClass(cls.slug)}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

// An embed cannot show the panel in place, so it opens the studio's own page
// with this param and that page opens the panel on load.
export const INSTRUCTOR_PARAM = 'instructor';
